/**
 * WhiteboardImportModal
 *
 * The whiteboard-photo analog of the Miro import flow (see ItemImportExportModal
 * and RoadmapMiroModal). A user uploads or snaps a photo of a physical
 * whiteboard; a vision model reads its boxes, sticky notes, arrows, lines, and
 * containers; and we map those signifiers onto platform Items and their
 * relationships — the same destination the Miro connector writes to.
 *
 * Wizard steps:
 *   upload   → choose / capture a photo
 *   analyzing→ vision model parses the board (whiteboardImportService)
 *   preview  → editable plan: items (title/category/altitude) + relationships,
 *              with a spatial mini-map and a signifier legend
 *   done     → summary of what was created
 *
 * On confirm we create Items via itemsStore and the drawn connections as
 * relationship edges via itemRelationshipsStore.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  X,
  Upload,
  Loader2,
  Camera,
  Video,
  AlertTriangle,
  CheckCircle,
  Check,
  Sparkles,
  ArrowRight,
  ArrowLeftRight,
  Trash2,
  Plus,
  Info,
  Layers,
  KeyRound,
  Play,
  LayoutGrid,
} from 'lucide-react';
import {
  analyzeWhiteboardPhotos,
  buildImportPlan,
  buildItemProvenance,
  normalizeWhiteboardImageInput,
  altitudeFromPosition,
  DEFAULT_COLOR_TO_CATEGORY,
  SIGNIFIER_LEGEND,
  type WhiteboardImportPlan,
  type PlannedItem,
  type PlannedRelationship,
  type WhiteboardUnrecognized,
} from '../../../lib/whiteboardImportService';
import { registerScenePositions } from '../../../lib/whiteboardRegistration';
import { WhiteboardCaptureCoach } from './WhiteboardCaptureCoach';
import { loadMiroColorMaps, getColorRulesForEntity, type MiroColorRule } from '../../../lib/fieldMappingRegistry';
import { describeSignifierRule } from '../../../lib/whiteboardSignifiers';
import { newImportBatchId } from '../../../lib/importBatch';
import { ImportBatchActions } from './ImportBatchActions';
import { WhiteboardLanguageEditor } from './WhiteboardLanguageEditor';
import type { ItemLanguageType } from '../../../types/prioritization/lifecycle';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import type { RelationshipEdgeType } from '../../../types/prioritization/itemRelationships';
import {
  assessImageQuality,
  recommendedPhotoCount,
  type CaptureQuality,
} from '../../../lib/whiteboardCaptureQuality';
import {
  mergeCapturedScene,
  boardBaselineFromPlan,
  type MergeResult,
  type BoardBaseline,
} from '../../../lib/whiteboardMerge';
import {
  saveBoardBaseline,
  deleteBoard,
  getBoard,
  loadBoards,
  type WhiteboardBoard,
} from '../../../lib/whiteboardBaselineStore';
import { saveBoardCapture, deleteBoardCapture } from '../../../lib/whiteboardCaptureStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';
import { useAuditStore } from '../../../stores/prioritization/auditStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId, isSupabaseAvailable, isAuthenticated } from '../../../lib/supabaseSync';
import {
  aiKeyPreview,
  saveMyAiKey,
  removeMyAiKey,
  useMyAiKeys,
  isManagedAnthropicEnabled,
  resolveAnthropicAuthMode,
  type AnthropicAuthMode,
} from '../../../lib/aiProviderKeys';
import { buildRevealScript, buildRevealScriptFromMerge, type RevealScript } from '../../../lib/whiteboardReveal';
import { sessionPrefillFromReveal } from '../../../lib/sessionPrefill';
import { WhiteboardRevealOverlay, type RevealView } from './reveal/WhiteboardRevealOverlay';
import { DEMO_BOARDS, type DemoBoard } from '../../../lib/whiteboardDemoBoards';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../lib/featureFlags';
import { DEFAULT_SEQUENCE_STRENGTH, isSequenceEdgeType } from '../../../lib/sequenceRelationships';

// ── Constants ─────────────────────────────────────────────────────────────

type Step = 'select-board' | 'upload' | 'analyzing' | 'preview' | 'diff' | 'done';
type Mode = 'new' | 'recapture';

const CATEGORY_OPTIONS: Array<{ value: ItemLanguageType; label: string }> = [
  { value: 'problem', label: 'Problem' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'risk', label: 'Risk' },
  { value: 'solution', label: 'Solution' },
  { value: 'commitment', label: 'Commitment' },
  { value: 'investment', label: 'Investment' },
];

const RELATIONSHIP_OPTIONS: Array<{ value: RelationshipEdgeType; label: string }> = [
  { value: 'dependency', label: 'Sequence (dependency)' },
  { value: 'prerequisite', label: 'Sequence (prerequisite)' },
  { value: 'related', label: 'Related' },
  { value: 'contains', label: 'Contains' },
  { value: 'breakdown', label: 'Breakdown' },
  { value: 'elaboration', label: 'Elaboration' },
  { value: 'aggregation', label: 'Aggregation' },
];

const COLOR_SWATCH: Record<string, string> = {
  yellow: '#FFD02F', orange: '#FF9800', red: '#F44336', pink: '#E91E63',
  green: '#4CAF50', light_green: '#90EE90', blue: '#2196F3', light_blue: '#87CEEB',
  cyan: '#00BCD4', purple: '#9C27B0', violet: '#9C27B0', gray: '#9E9E9E',
  white: '#FFFFFF', unknown: '#E5E7EB',
};

const MAX_IMAGE_BYTES = 12 * 1024 * 1024; // Anthropic image limit headroom

/** One captured photo in a "walk the wall" set. */
interface CaptureTile {
  id: string;
  dataUrl: string;
  fileName: string;
  quality: CaptureQuality | null;
}

/** Tailwind text/bg per quality level, for the per-tile badge. */
const QUALITY_STYLE: Record<CaptureQuality['level'], { dot: string; label: string }> = {
  good: { dot: 'bg-[var(--green)]', label: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  warn: { dot: 'bg-[var(--mustard)]', label: 'text-[var(--mustard)]' },
  poor: { dot: 'bg-[var(--coral)]', label: 'text-[var(--coral)]' },
};

/** Working-image long edge for the quality check (keeps the analysis cheap and the thresholds stable). */
const QUALITY_WORKING_LONG_EDGE = 512;

/**
 * Downsample a photo onto a small canvas and run the pure capture-quality
 * assessment. Resolves null if the browser can't read the pixels.
 */
function evaluateCaptureQuality(dataUrl: string): Promise<CaptureQuality | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
      if (longEdge === 0) { resolve(null); return; }
      const scale = Math.min(1, QUALITY_WORKING_LONG_EDGE / longEdge);
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(img, 0, 0, w, h);
      try {
        const imgData = ctx.getImageData(0, 0, w, h);
        resolve(assessImageQuality({ data: imgData.data, width: w, height: h }, longEdge));
      } catch {
        resolve(null); // cross-origin / tainted canvas — skip silently
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

// ── Spatial mini-map ────────────────────────────────────────────────────────

/**
 * Tiny scatter of the board's nodes using their normalized positions, so the
 * user can sanity-check that spatial layout (and thus altitude mapping) was
 * read correctly before importing.
 */
function SpatialMiniMap({ items }: { items: PlannedItem[] }) {
  if (items.length === 0) return null;
  return (
    <div className="relative w-full h-40 border border-[var(--rule)] bg-[var(--surface)] overflow-hidden">
      <span className="absolute top-1 left-2 text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Strategic ↑</span>
      <span className="absolute bottom-1 left-2 text-[10px] uppercase tracking-wide text-[var(--text-ter)]">Tactical ↓</span>
      {items.map((it) => (
        <div
          key={it.itemId}
          title={`${it.title} — altitude ${it.altitudeLevel ?? '—'}`}
          className="absolute -translate-x-1/2 -translate-y-1/2 border border-[var(--rule)]/60 shadow-sm"
          style={{
            left: `${it.position.x * 100}%`,
            top: `${it.position.y * 100}%`,
            width: 14,
            height: 10,
            background: COLOR_SWATCH[it.color] ?? COLOR_SWATCH.unknown,
            opacity: it.include ? 1 : 0.25,
          }}
        />
      ))}
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────

/**
 * The captured photo with a tappable marker over each recognized object, so you
 * can deselect things in the drawing you don't want imported (a legend scribble,
 * a stray doodle, someone's coffee-ring annotation) by looking at the actual
 * board rather than a text list. Included markers show their category color;
 * excluded ones fade and show a slash. Positions are the scene's normalized
 * coordinates, so they land on the photo regardless of its resolution.
 *
 * Shown only for a single-photo capture — a multi-tile "walk the wall" set has
 * no single backdrop to overlay, so that case keeps the abstract mini-map.
 */
function PhotoSelectionOverlay({
  imageUrl,
  items,
  onToggle,
}: {
  imageUrl: string;
  items: PlannedItem[];
  onToggle: (ref: string, include: boolean) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-center border border-[var(--rule)] bg-[var(--bg)] overflow-hidden">
        <div className="relative inline-block">
          <img src={imageUrl} alt="Captured whiteboard" className="block max-h-[420px] w-auto" />
          {items.map((it) => (
            <button
              key={it.ref}
              type="button"
              onClick={() => onToggle(it.ref, !it.include)}
              title={`${it.title}${it.include ? ' — tap to exclude' : ' (excluded) — tap to include'}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full flex items-center justify-center shadow-md transition-transform hover:scale-110 ${
                it.include ? 'ring-2 ring-white' : 'opacity-70'
              }`}
              style={{
                left: `${it.position.x * 100}%`,
                top: `${it.position.y * 100}%`,
                background: it.include ? (COLOR_SWATCH[it.color] ?? COLOR_SWATCH.unknown) : 'rgba(17,24,39,0.75)',
              }}
            >
              {it.include
                ? <Check className="h-3.5 w-3.5 text-white drop-shadow" />
                : <X className="h-3.5 w-3.5 text-white" />}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-[var(--text-ter)] text-center">
        Tap an object on the photo to exclude it from the import (or bring it back).
      </p>
    </div>
  );
}

/**
 * A zoomed thumbnail of the captured photo around one item's position, so a
 * user can proofread the transcription against the actual handwriting at a
 * glance. Implemented as a CSS background crop (no canvas): the window is a
 * fixed fraction of the image centered on the item, scaled to fill the
 * viewport. Slight aspect distortion is fine for a verify-the-text thumbnail.
 */
function PhotoCrop({ imageUrl, position, title }: { imageUrl: string; position: { x: number; y: number }; title?: string }) {
  const VW = 76;   // viewport width (px)
  const VH = 56;   // viewport height (px)
  const FW = 0.26; // crop window width as a fraction of the image
  const FH = 0.20; // crop window height as a fraction of the image
  const sw = VW / FW;
  const sh = VH / FH;
  return (
    <span
      aria-hidden
      title={title}
      className="flex-shrink-0 rounded border border-[var(--rule)] bg-[var(--hover-tint)] overflow-hidden"
      style={{
        width: VW,
        height: VH,
        backgroundImage: `url("${imageUrl}")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: `${sw}px ${sh}px`,
        backgroundPosition: `${VW / 2 - sw * position.x}px ${VH / 2 - sh * position.y}px`,
      }}
    />
  );
}

/** Counts up to `target` once on mount — a small bit of delight on the reveal. */
function CountUp({ target, durationMs = 550 }: { target: number; durationMs?: number }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target <= 0) { setN(0); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // ease-out so it decelerates into the final value
      setN(Math.round((1 - Math.pow(1 - t, 3)) * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return <>{n}</>;
}

/**
 * The "what changed since v1" reveal — the payoff of a re-capture. Big animated
 * counts so the round-trip lands as a moment, not a sentence.
 */
function DiffReveal({ summary }: { summary: MergeResult['summary'] }) {
  const changed = summary.renamed + summary.moved;
  const tiles = [
    { label: 'new', value: summary.added, cls: 'text-[var(--green)] dark:text-[var(--text-ter)]', bg: 'bg-[var(--green-dim)] border-[var(--green)]' },
    { label: 'changed', value: changed, cls: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] border-[var(--slate)]' },
    { label: 'unchanged', value: summary.unchanged, cls: 'text-[var(--text-ter)]', bg: 'bg-[var(--surface)] border-[var(--rule)]' },
    { label: 'removed', value: summary.removed, cls: 'text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]' },
  ].filter((t) => t.label !== 'removed' || t.value > 0);

  const headline =
    summary.added + changed + summary.removed === 0
      ? 'No changes since the last version'
      : `${summary.added + changed + summary.removed} change${summary.added + changed + summary.removed !== 1 ? 's' : ''} since v1`;

  return (
    <div className="border border-primary-100 bg-gradient-to-br from-primary-50/70 to-white px-4 py-3 animate-fadeIn">
      <div className="flex items-center gap-1.5 text-xs font-medium text-primary-700 mb-2">
        <Sparkles className="h-3.5 w-3.5" /> {headline}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {tiles.map((t) => (
          <div key={t.label} className={` border ${t.bg} px-3 py-2 text-center`}>
            <div className={`text-2xl font-semibold tabular-nums ${t.cls}`}><CountUp target={t.value} /></div>
            <div className="text-[11px] uppercase tracking-wide text-[var(--text-ter)]">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Shared dark-mode-safe styling for amber callout panels in this modal.
 * Global `.dark .bg-*` remaps in index.css make light-only Tailwind illegible.
 */
const AMBER_CALLOUT =
  ' border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40';
const AMBER_CALLOUT_TITLE = 'text-[var(--mustard)] dark:text-[var(--mustard)]';
const AMBER_CALLOUT_BODY = 'text-[var(--mustard)] dark:text-[var(--mustard)]/90';

/**
 * The "what should I do with these?" list for marks the model couldn't classify.
 * Shared by the new-import preview and the re-capture diff. Each mark can be
 * promoted to a new item or ignored.
 */
function UnrecognizedList({
  marks,
  onResolve,
}: {
  marks: WhiteboardUnrecognized[];
  onResolve: (id: string, action: 'add' | 'ignore') => void;
}) {
  if (marks.length === 0) return null;
  return (
    <div className={`${AMBER_CALLOUT} p-3`}>
      <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${AMBER_CALLOUT_TITLE} mb-2`}>
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> Not sure about these — what should I do?
      </div>
      <div className="space-y-1.5">
        {marks.map((u) => (
          <div
            key={u.id}
            className="flex items-center gap-2 text-sm border border-[var(--mustard)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-2.5 py-1.5"
          >
            <span className="px-1.5 py-0.5 rounded bg-[var(--mustard-dim)]/80 dark:bg-[var(--mustard)]/35 text-[var(--mustard)] dark:text-[var(--mustard)] text-[10px] font-semibold uppercase tracking-wide flex-shrink-0">
              {u.kind}
            </span>
            <span className="text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{u.description}</span>
            <div className="ml-auto flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => onResolve(u.id, 'add')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-primary-700 dark:text-[#8EA7D8] hover:bg-primary-50 dark:hover:bg-[var(--active-tint)]"
                title="Add as a new item (you'll name it)"
              >
                <Plus className="h-3.5 w-3.5" /> Add as item
              </button>
              <button
                type="button"
                onClick={() => onResolve(u.id, 'ignore')}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[var(--text-sec)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
                title="Ignore this mark"
              >
                <X className="h-3.5 w-3.5" /> Ignore
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface Props {
  onClose: () => void;
  /** Called after items are created so callers can refresh if needed. */
  onImported?: (count: number) => void;
  /** 'recapture' updates an existing saved board (v1 → v2 diff); default 'new'. */
  initialMode?: Mode;
  /** Pre-selected board to re-capture (skips the board picker). */
  recaptureBoardId?: string;
  /**
   * When true (opened from the Import Items chooser), Cancel on the first step
   * returns to the source list instead of closing the whole import flow.
   */
  returnToSourceChooser?: boolean;
}

export function WhiteboardImportModal({
  onClose,
  onImported,
  initialMode = 'new',
  recaptureBoardId,
  returnToSourceChooser = false,
}: Props) {
  const createItem = useItemsStore((s) => s.createItem);
  const updateItem = useItemsStore((s) => s.updateItem);
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const updateEdge = useItemRelationshipsStore((s) => s.updateEdge);
  const dissolveEdge = useItemRelationshipsStore((s) => s.dissolveEdge);
  const getEdgesForItem = useItemRelationshipsStore((s) => s.getEdgesForItem);
  const createCollection = useCollectionsStore((s) => s.createCollection);
  const addItemToCollection = useCollectionsStore((s) => s.addItemToCollection);
  const deleteCollection = useCollectionsStore((s) => s.deleteCollection);
  const addNotification = useUIStore((s) => s.addNotification);
  const userId = useUserStore((s) => s.userId);
  const navigate = useNavigate();

  const mode = initialMode;
  const [boards, setBoards] = useState<WhiteboardBoard[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(initialMode === 'recapture' && !recaptureBoardId);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(recaptureBoardId ?? null);
  const [merge, setMerge] = useState<MergeResult | null>(null);
  // Unrecognized marks from a re-capture scene (the merge result doesn't carry
  // them); surfaced in the diff with the same add/ignore actions as a fresh import.
  const [recaptureUnrecognized, setRecaptureUnrecognized] = useState<WhiteboardUnrecognized[]>([]);
  // True when the re-capture's corner registration marks were detected and used
  // to align recognized positions to the board frame (projection-aware capture).
  const [registered, setRegistered] = useState(false);
  // Per-change review on the v1→v2 diff: which detected changes to skip, and
  // which vanished items to archive (default: apply all changes, archive none).
  const [excludedItemIds, setExcludedItemIds] = useState<Set<string>>(new Set());
  const [excludedRelIdx, setExcludedRelIdx] = useState<Set<number>>(new Set());
  const [archiveRemovedIds, setArchiveRemovedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (initialMode !== 'recapture' || recaptureBoardId) return;
    let active = true;
    loadBoards().then((b) => {
      if (active) { setBoards(b); setBoardsLoading(false); }
    });
    return () => { active = false; };
  }, [initialMode, recaptureBoardId]);

  const [step, setStep] = useState<Step>(
    initialMode === 'recapture' ? (recaptureBoardId ? 'upload' : 'select-board') : 'upload',
  );
  const managedKeyEnabled = isManagedAnthropicEnabled();
  // A person's own key is kept on the server (migration 982); re-render when it
  // is saved, removed or loaded.
  useMyAiKeys();
  const authMode: AnthropicAuthMode = resolveAnthropicAuthMode();
  const keyPreview = aiKeyPreview('anthropic');
  const [showByokForm, setShowByokForm] = useState(false);
  // Analysis can run if the user brought a key OR the deployment offers a managed
  // (shared / workspace) Claude key.
  const canAnalyze = authMode !== 'none';
  const [keyInput, setKeyInput] = useState('');
  const saveKey = async () => {
    const k = keyInput.trim();
    if (!k) return;
    const failed = await saveMyAiKey('anthropic', k);
    // The dialog's own error line (declared below; read when this runs).
    setError(failed);
    if (failed) return;
    setKeyInput('');
    setShowByokForm(false);
  };
  const switchToWorkspaceKey = async () => {
    const failed = await removeMyAiKey('anthropic');
    setError(failed);
    if (failed) return;
    setKeyInput('');
    setShowByokForm(false);
  };
  // A board can span a wall, so capture is a set of overlapping "walk the wall" tiles.
  const [tiles, setTiles] = useState<CaptureTile[]>([]);
  const [wallWidthM, setWallWidthM] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  const [plan, setPlan] = useState<WhiteboardImportPlan | null>(null);
  const [mapAltitude, setMapAltitude] = useState(true);
  const [mapColor, setMapColor] = useState(true);

  // Group the board's items into a named collection so it stays a navigable unit.
  const [groupIntoCollection, setGroupIntoCollection] = useState(true);
  const [collectionName, setCollectionName] = useState('');

  const [created, setCreated] = useState<{
    items: number;
    linked: number;
    relationships: number;
    collection: string | null;
  }>({
    items: 0,
    linked: 0,
    relationships: 0,
    collection: null,
  });
  const [processing, setProcessing] = useState(false);

  // What a fresh import created, so it can be undone in one click from the done
  // screen (only set for the new-import path — re-capture mutates existing items
  // and isn't cleanly reversible). null once undone.
  const [undoRecord, setUndoRecord] = useState<{ batchId: string; itemIds: string[]; edgeIds: string[]; collectionId?: string; boardId?: string } | null>(null);
  const [undone, setUndone] = useState(false);

  // Post-commit reveal (feature-flagged, presentation only): the script is a
  // pure projection of committed state (lib/whiteboardReveal), built at each
  // exit point. It auto-plays once and can be replayed / stepped / exported
  // from the done screen. It never influences parse, plan, or commit.
  const revealEnabled = useFeatureFlag(FEATURE_FLAGS.WHITEBOARD_REVEAL);
  const [revealScript, setRevealScript] = useState<RevealScript | null>(null);
  const [revealView, setRevealView] = useState<RevealView | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [coachOpen, setCoachOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  // Items must persist to a workspace, which requires a real Supabase session;
  // an anonymous session is rejected by RLS (rows would only live on the device).
  // Gate creation on sign-in when Supabase is configured. (Local/offline mode —
  // no Supabase — keeps working with local-only writes.)
  const [needsSignIn, setNeedsSignIn] = useState(false);
  useEffect(() => {
    if (!isSupabaseAvailable()) { setNeedsSignIn(false); return; }
    let active = true;
    isAuthenticated().then((ok) => { if (active) setNeedsSignIn(!ok); });
    return () => { active = false; };
  }, []);

  // The workspace's shared visual-language rules (same config as Miro's 'items'
  // color map). Applied during import so cues like fill/border/text color and
  // shape set item attributes (e.g. green fill → impact 10).
  const [signifierRules, setSignifierRules] = useState<MiroColorRule[]>([]);
  useEffect(() => {
    const ws = getCurrentWorkspaceId();
    if (!ws) return;
    let active = true;
    loadMiroColorMaps(ws)
      .then((maps) => { if (active) setSignifierRules(getColorRulesForEntity(maps, 'items')); })
      .catch(() => { /* no mapping configured — defaults apply */ });
    return () => { active = false; };
  }, []);

  // ── File handling ─────────────────────────────────────────────────────────

  const addFile = useCallback((file: File) => {
    if (!/^image\//.test(file.type)) {
      setError('Please choose image files (JPEG or PNG).');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('One image is over 12 MB — use a smaller or more compressed photo.');
      return;
    }
    const id = crypto.randomUUID();
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setTiles((prev) => [...prev, { id, dataUrl, fileName: file.name, quality: null }]);
      void evaluateCaptureQuality(dataUrl).then((q) =>
        setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, quality: q } : t))),
      );
    };
    reader.onerror = () => setError('Could not read one of those files. Try a different photo.');
    reader.readAsDataURL(file);
  }, []);

  const addFiles = useCallback((files: FileList | File[]) => {
    setError(null);
    Array.from(files).forEach(addFile);
  }, [addFile]);

  // Add a frame captured by the live coach (already a JPEG data URL).
  const addDataUrlTile = useCallback((dataUrl: string) => {
    setError(null);
    const id = crypto.randomUUID();
    setTiles((prev) => [...prev, { id, dataUrl, fileName: `capture-${prev.length + 1}.jpg`, quality: null }]);
    void evaluateCaptureQuality(dataUrl).then((q) =>
      setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, quality: q } : t))),
    );
  }, []);

  const removeTile = useCallback((id: string) => {
    setTiles((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  // ── Analyze ─────────────────────────────────────────────────────────────

  const runAnalysis = useCallback(async () => {
    if (tiles.length === 0) return;
    setStep('analyzing');
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Normalize each tile (HEIC/large → JPEG) before sending to vision.
      const images = await Promise.all(tiles.map((t) => normalizeWhiteboardImageInput(t.dataUrl)));
      const rawScene = await analyzeWhiteboardPhotos(images, {
        signal: controller.signal,
        workspaceId: getCurrentWorkspaceId(),
      });
      if (rawScene.nodes.length === 0) {
        setError(
          rawScene.warnings[0] ??
            'No shapes were recognized in that photo. Make sure the whiteboard fills the frame and is well lit.',
        );
        setStep('upload');
        return;
      }

      // If the board was projected with corner registration marks and they were
      // all detected, remap recognized positions from photo space into the
      // board's coordinate frame so new boxes land correctly relative to the
      // projected v1 — regardless of the camera's angle or crop. A no-op when no
      // fiducials are present.
      const scene = registerScenePositions(rawScene);
      setRegistered(Boolean(rawScene.fiducials));

      // Re-capture: diff the new scene against the chosen board's stored v1.
      if (mode === 'recapture' && selectedBoardId) {
        const board = await getBoard(selectedBoardId);
        if (!board) {
          setError('That board could not be found. It may have been removed.');
          setStep('select-board');
          return;
        }
        setMerge(mergeCapturedScene(board.baseline, scene, { signifierRules }));
        setRecaptureUnrecognized(scene.unrecognized ?? []);
        setExcludedItemIds(new Set());
        setExcludedRelIdx(new Set());
        setArchiveRemovedIds(new Set());
        setStep('diff');
        return;
      }

      // Check each recognized box against items the workspace already has, so a
      // re-photographed board reuses existing items instead of cloning them.
      const existingItems = useItemsStore
        .getState()
        .items.filter((i) => i.title)
        .map((i) => ({ id: i.id, title: i.title as string }));
      setPlan(
        buildImportPlan(scene, {
          mapAltitudeFromPosition: mapAltitude,
          mapColorToCategory: mapColor,
          signifierRules,
          existingItems,
        }),
      );
      setCollectionName(
        (scene.title?.trim() || `Whiteboard — ${new Date().toLocaleDateString()}`).slice(0, 120),
      );
      setStep('preview');
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Photo analysis failed.');
      setStep('upload');
    } finally {
      abortRef.current = null;
    }
  }, [tiles, mapAltitude, mapColor, mode, selectedBoardId, signifierRules]);

  // Demo mode: skip the camera and the vision call by injecting a canned,
  // pre-recognized scene, then continue through the SAME plan → preview →
  // commit pipeline a live photo uses (duplicate detection, the workspace's
  // signifier rules, collection naming, undo — all identical). This is the
  // post-analysis half of runAnalysis with the fixture standing in for the
  // vision model's output.
  const runDemoBoard = useCallback((demo: DemoBoard) => {
    setError(null);
    setTiles([{ id: crypto.randomUUID(), dataUrl: demo.photoDataUrl, fileName: `${demo.id}.svg`, quality: null }]);
    const existingItems = useItemsStore
      .getState()
      .items.filter((i) => i.title)
      .map((i) => ({ id: i.id, title: i.title as string }));
    setPlan(
      buildImportPlan(demo.scene, {
        mapAltitudeFromPosition: mapAltitude,
        mapColorToCategory: mapColor,
        signifierRules,
        existingItems,
      }),
    );
    setCollectionName(
      (demo.scene.title?.trim() || `Whiteboard — ${new Date().toLocaleDateString()}`).slice(0, 120),
    );
    setStep('preview');
  }, [mapAltitude, mapColor, signifierRules]);

  // Mapping toggles re-derive a field across every item in place (deterministic,
  // reusing the same pure helpers buildImportPlan uses). Altitude is a function
  // of position; category is a function of color.
  const applyAltitudeMapping = useCallback((enabled: boolean) => {
    setMapAltitude(enabled);
    setPlan((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((it) => ({
              ...it,
              altitudeLevel: enabled ? altitudeFromPosition(it.position.y) : undefined,
            })),
          }
        : prev,
    );
  }, []);

  const applyColorMapping = useCallback((enabled: boolean) => {
    setMapColor(enabled);
    setPlan((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((it) => ({
              ...it,
              itemCategory: enabled ? DEFAULT_COLOR_TO_CATEGORY[it.color] : undefined,
            })),
          }
        : prev,
    );
  }, []);

  // ── Plan editing helpers ────────────────────────────────────────────────

  const patchItem = (ref: string, patch: Partial<PlannedItem>) => {
    setPlan((prev) =>
      prev ? { ...prev, items: prev.items.map((it) => (it.ref === ref ? { ...it, ...patch } : it)) } : prev,
    );
  };
  const patchRel = (ref: string, patch: Partial<PlannedRelationship>) => {
    setPlan((prev) =>
      prev
        ? { ...prev, relationships: prev.relationships.map((r) => (r.ref === ref ? { ...r, ...patch } : r)) }
        : prev,
    );
  };

  // Resolve a mark the model couldn't classify: either promote it to a new item
  // (flagged for review so the user names it) or dismiss it. Either way it leaves
  // the "unsure" list.
  const resolveUnrecognized = (id: string, action: 'add' | 'ignore') => {
    setPlan((prev) => {
      if (!prev) return prev;
      const mark = prev.unrecognized.find((u) => u.id === id);
      const unrecognized = prev.unrecognized.filter((u) => u.id !== id);
      if (action === 'ignore' || !mark) return { ...prev, unrecognized };
      const newItem: PlannedItem = {
        ref: `unrec-${id}`,
        itemId: crypto.randomUUID(),
        title: mark.description,
        recognizedTitle: mark.description,
        color: 'unknown',
        shape: 'other',
        position: mark.position ?? { x: 0.5, y: 0.5 },
        confidence: 0,
        needsReview: true,
        include: true,
        resolution: 'create',
      };
      return { ...prev, unrecognized, items: [...prev.items, newItem] };
    });
  };

  // Same, for the re-capture diff: promote a mark into a new merged item (so it's
  // created on apply) or ignore it. Bumps the reveal's "new" count to match.
  const resolveRecaptureUnrecognized = (id: string, action: 'add' | 'ignore') => {
    const mark = recaptureUnrecognized.find((u) => u.id === id);
    setRecaptureUnrecognized((prev) => prev.filter((u) => u.id !== id));
    if (action === 'ignore' || !mark) return;
    setMerge((prev) =>
      prev
        ? {
            ...prev,
            items: [
              ...prev.items,
              {
                itemId: crypto.randomUUID(),
                title: mark.description,
                position: mark.position ?? { x: 0.5, y: 0.5 },
                status: 'new',
                color: 'unknown',
              },
            ],
            summary: { ...prev.summary, added: prev.summary.added + 1 },
          }
        : prev,
    );
  };

  const itemsByRef = useMemo(() => {
    const map = new Map<string, PlannedItem>();
    plan?.items.forEach((it) => map.set(it.ref, it));
    return map;
  }, [plan]);

  const poorTileCount = tiles.filter((t) => t.quality?.level === 'poor').length;

  const includedItems = plan?.items.filter((it) => it.include) ?? [];
  /** Included nodes that will create a brand-new item (vs. reuse an existing one). */
  const newItemCount = includedItems.filter((it) => it.resolution === 'create').length;
  const reusedCount = includedItems.filter((it) => it.resolution === 'use_existing').length;
  /** Included items whose transcription is still unverified low-confidence. */
  const reviewCount = includedItems.filter((it) => it.needsReview).length;
  const includedRels =
    plan?.relationships.filter(
      (r) => r.include && itemsByRef.get(r.sourceRef)?.include && itemsByRef.get(r.targetRef)?.include,
    ) ?? [];

  // ── Confirm import ────────────────────────────────────────────────────────

  const confirmImport = useCallback(async () => {
    if (!plan) return;
    if (needsSignIn) { setError('Sign in to save these items to your workspace.'); return; }
    setProcessing(true);
    const workspaceId = getCurrentWorkspaceId() ?? undefined;

    // Resolve every included node to the platform item id its relationships
    // should attach to: a newly created id, or a matched existing item's id.
    const logItemImport = useAuditStore.getState().logItemImport;
    // Record where this item came from on the board, on its Activity timeline.
    const logProvenance = (itemId: string, it: PlannedItem) => {
      try {
        const { note, details } = buildItemProvenance({
          boardTitle: plan.title,
          recognizedText: it.recognizedTitle,
          finalTitle: it.title,
          color: it.color,
          shape: it.shape,
          position: it.position,
          reused: it.resolution === 'use_existing',
          matchedTitle: it.duplicateOf?.existingTitle,
          confidence: it.confidence,
        });
        logItemImport({
          type: 'item_imported',
          itemId,
          source: 'whiteboard_photo',
          actorId: userId ?? 'unknown',
          actorRole: 'creator',
          note,
          details,
        });
      } catch {
        /* provenance is best-effort */
      }
    };

    const refToItemId = new Map<string, string>();
    // One id for this import run, stamped on every item it creates
    // (items.import_batch_id) — drives "View these N items" and the undo.
    const importBatchId = newImportBatchId();
    const createdItemIds: string[] = [];
    const createdEdgeIds: string[] = [];
    let itemCount = 0;
    let itemFailed = 0;
    let linkedCount = 0;
    for (const it of plan.items) {
      if (!it.include) continue;
      if (it.resolution === 'use_existing' && it.duplicateOf) {
        refToItemId.set(it.ref, it.duplicateOf.existingItemId);
        linkedCount++;
        logProvenance(it.duplicateOf.existingItemId, it);
        continue;
      }
      if (!it.title.trim()) continue;
      refToItemId.set(it.ref, it.itemId);
      try {
        // Await persistence so relationship edges and collection memberships
        // don't race the items FK (fire-and-forget create used to drop both).
        const ok = await createItem({
          id: it.itemId,
          title: it.title.trim(),
          description: it.description,
          source: 'Whiteboard photo',
          tags: ['whiteboard-import'],
          importBatchId,
          ...(it.itemCategory && { itemCategory: it.itemCategory }),
          ...(it.altitudeLevel && { altitudeLevel: it.altitudeLevel }),
          ...((it.metadata && Object.keys(it.metadata).length) || it.code
            ? { metadata: { ...(it.metadata ?? {}), ...(it.code ? { whiteboardAnchor: it.code } : {}) } }
            : {}),
        } as Parameters<typeof createItem>[0]);
        if (!ok) {
          itemFailed++;
          refToItemId.delete(it.ref);
          continue;
        }
        createdItemIds.push(it.itemId);
        itemCount++;
        logProvenance(it.itemId, it);
      } catch {
        itemFailed++;
        refToItemId.delete(it.ref);
        /* continue on individual failures */
      }
    }

    // Existing edges → don't recreate a relationship the workspace already has
    // (keeps re-import of an evolving board from piling up duplicate links).
    const existingEdges = useItemRelationshipsStore.getState().edges;
    const edgeExists = (src: string, tgt: string, type: PlannedRelationship['type'], directed: boolean) =>
      existingEdges.some(
        (e) =>
          e.type === type &&
          ((e.sourceItemId === src && e.targetItemId === tgt) ||
            (!directed && e.sourceItemId === tgt && e.targetItemId === src)),
      );

    let relCount = 0;
    let relFailed = 0;
    for (const rel of plan.relationships) {
      if (!rel.include) continue;
      const sourceId = refToItemId.get(rel.sourceRef);
      const targetId = refToItemId.get(rel.targetRef);
      if (!sourceId || !targetId || sourceId === targetId) continue;
      if (edgeExists(sourceId, targetId, rel.type, rel.directionality === 'directed')) continue;
      try {
        // Drop inverted Sequence leftovers between the same pair (boards
        // captured before dependency endpoints were oriented for Sequence &
        // conditions). dissolveEdge is best-effort.
        if (rel.type === 'dependency' || rel.type === 'prerequisite') {
          const leftovers = getEdgesForItem(sourceId).filter(
            (e) =>
              (e.type === 'dependency' || e.type === 'prerequisite') &&
              e.status !== 'dissolved' &&
              e.sourceItemId === targetId &&
              e.targetItemId === sourceId,
          );
          for (const leftover of leftovers) {
            try { await dissolveEdge(leftover.id); } catch { /* continue */ }
          }
        }
        const edge = await createEdge({
          type: rel.type,
          // A sequence edge states its strength; a drawn line has none, so the floor (soft).
          ...(isSequenceEdgeType(rel.type) ? { subtype: DEFAULT_SEQUENCE_STRENGTH } : {}),
          directionality: rel.directionality,
          status: 'active',
          declaredMode: 'declared',
          sourceItemId: sourceId,
          targetItemId: targetId,
          label: rel.label,
          rationale: 'Imported from whiteboard photo',
          metadata: {
            source: 'whiteboard_import',
            fromContainment: rel.fromContainment,
            // Uncertainty travels with the edge (semantic-connectors 02-DESIGN §5).
            confidence: rel.confidence,
            basis: rel.basis,
          },
          ...(workspaceId && { workspaceId }),
          ...(userId && { createdBy: userId }),
        });
        if (edge) { relCount++; createdEdgeIds.push(edge.id); }
        else relFailed++;
      } catch {
        relFailed++;
      }
    }

    // Product Collections page / item "In collection" filters use collectionsStore
    // (not item_relationship_groups type=collection).
    const memberItemIds = Array.from(new Set(refToItemId.values()));
    let collectionTitle: string | null = null;
    let collectionId: string | null = null;
    const trimmedName = collectionName.trim();
    if (groupIntoCollection && trimmedName && memberItemIds.length > 0) {
      try {
        const collection = await createCollection(
          trimmedName,
          'Imported from whiteboard photo — captured together from one board',
          userId ?? undefined,
        );
        if (collection) {
          collectionTitle = trimmedName;
          collectionId = collection.id;
          for (const itemId of memberItemIds) {
            await addItemToCollection(collection.id, itemId, userId ?? undefined);
          }
        }
      } catch {
        /* collection is best-effort; items/relationships already landed */
      }
    }

    // Snapshot v1 so this board can be re-captured (v1 → v2) later.
    const savedBoardId = collectionId ?? crypto.randomUUID();
    try {
      const saved = await saveBoardBaseline(savedBoardId, trimmedName || plan.title || 'Whiteboard', boardBaselineFromPlan(plan, savedBoardId));
      void saveBoardCapture(savedBoardId, tiles.map((t) => t.dataUrl), saved.versionNumber);
    } catch {
      /* baseline snapshot is best-effort */
    }

    setCreated({ items: itemCount, linked: linkedCount, relationships: relCount, collection: collectionTitle });
    setUndoRecord({ batchId: importBatchId, itemIds: createdItemIds, edgeIds: createdEdgeIds, collectionId: collectionId ?? undefined, boardId: savedBoardId });
    setUndone(false);
    setProcessing(false);
    setStep('done');
    if (revealEnabled) {
      setRevealScript(buildRevealScript(plan, { photoUrl: tiles[0]?.dataUrl, collection: collectionTitle }));
      setRevealView('auto');
    }
    onImported?.(itemCount);
    const linkedNote = linkedCount > 0 ? `, ${linkedCount} reused` : '';
    // Items that failed to save used to be dropped silently, so a capture that
    // wrote nothing still announced itself as a success. Report them, and let
    // them decide the severity.
    const nothingLanded = itemCount === 0 && linkedCount === 0;
    const itemNote = itemFailed > 0
      ? ` — ${itemFailed} item${itemFailed !== 1 ? 's' : ''} could not be saved`
      : '';
    const relNote = relFailed > 0
      ? ` (${relFailed} sequence link${relFailed !== 1 ? 's' : ''} failed to save)` : '';
    addNotification({
      type: itemFailed > 0 || relFailed > 0 ? 'warning' : 'success',
      title: nothingLanded ? 'Whiteboard import failed' : 'Whiteboard imported',
      message: `${itemCount} item${itemCount !== 1 ? 's' : ''} created${linkedNote}, ${relCount} relationship${relCount !== 1 ? 's' : ''} added${collectionTitle ? ` · grouped as “${collectionTitle}”` : ''}${itemNote}${relNote}.`,
      duration: itemFailed > 0 ? 8000 : 4000,
    });
  }, [plan, needsSignIn, createItem, createEdge, dissolveEdge, getEdgesForItem, createCollection, addItemToCollection, addNotification, onImported, userId, groupIntoCollection, collectionName, tiles, revealEnabled]);

  // Reverse a just-completed fresh import. Items are never hard-deleted (owner
  // ruling 2026-09-22): ImportBatchActions asks for one reason and retires the
  // batch through retire_new_item. Before that, dissolve the edges and drop the
  // collection this import made — active relationships would block the retire
  // (canon 12 §599). Best-effort per element so one failure doesn't strand the rest.
  const beforeUndoImport = useCallback(async () => {
    if (!undoRecord) return;
    const { edgeIds, collectionId } = undoRecord;
    for (const id of edgeIds) { try { await dissolveEdge(id); } catch { /* continue */ } }
    if (collectionId) {
      try { await deleteCollection(collectionId); } catch { /* continue */ }
    }
    // Edges and collection are gone now; a retry only needs the items.
    setUndoRecord((prev) => (prev ? { ...prev, edgeIds: [], collectionId: undefined } : prev));
  }, [undoRecord, dissolveEdge, deleteCollection]);

  const afterUndoImport = useCallback(
    async (outcome: { retired: string[]; failed: Array<{ id: string }> }) => {
      onImported?.(0); // nudge the caller to refresh its list
      if (!undoRecord) return;
      if (outcome.failed.length > 0) {
        // Keep the undo available for what is still there, so it can be retried.
        setUndoRecord({ ...undoRecord, itemIds: outcome.failed.map((f) => f.id), edgeIds: [], collectionId: undefined });
        return;
      }
      if (undoRecord.boardId) {
        try { await deleteBoard(undoRecord.boardId); } catch { /* continue */ }
        void deleteBoardCapture(undoRecord.boardId);
      }
      setUndone(true);
      setUndoRecord(null);
      setRevealScript(null); // nothing left to reveal or replay
      setRevealView(null);
    },
    [undoRecord, onImported],
  );

  // ── Apply a re-capture merge (v1 → v2) ─────────────────────────────────────

  const applyMerge = useCallback(async () => {
    if (!merge || !selectedBoardId) return;
    if (needsSignIn) { setError('Sign in to save these changes to your workspace.'); return; }
    const board = await getBoard(selectedBoardId);
    if (!board) return;
    setProcessing(true);
    const workspaceId = getCurrentWorkspaceId() ?? undefined;

    // Items: create new, rename/move existing in place (same ids). Skip any the
    // user unchecked in the diff review.
    let added = 0;
    let addFailed = 0;
    let updated = 0;
    const recaptureBatchId = newImportBatchId();
    for (const it of merge.items) {
      if (excludedItemIds.has(it.itemId)) continue;
      try {
        if (it.status === 'new') {
          const ok = await createItem({
            id: it.itemId,
            title: it.title,
            source: 'Whiteboard photo (re-capture)',
            tags: ['whiteboard-import'],
            importBatchId: recaptureBatchId,
            ...(it.itemCategory && { itemCategory: it.itemCategory }),
            ...(it.altitudeLevel && { altitudeLevel: it.altitudeLevel }),
            ...(it.metadata && Object.keys(it.metadata).length ? { metadata: it.metadata } : {}),
          } as Parameters<typeof createItem>[0]);
          if (ok) added++; else addFailed++;
        } else if (it.status === 'renamed') {
          updateItem(it.itemId, { title: it.title });
          updated++;
        } else if (it.status === 'moved' && it.altitudeLevel) {
          updateItem(it.itemId, { altitudeLevel: it.altitudeLevel } as Partial<Parameters<typeof updateItem>[1]>);
          updated++;
        }
      } catch {
        /* continue on individual failures */
      }
    }

    // Archive items the user chose to retire (those that vanished from the board).
    let archived = 0;
    for (const r of merge.removedItems) {
      if (!archiveRemovedIds.has(r.id)) continue;
      try {
        updateItem(r.id, { exit_status: 'archived' } as Partial<Parameters<typeof updateItem>[1]>);
        archived++;
      } catch {
        /* continue */
      }
    }

    // Relationships: add new, retype existing edges in place. Skip unchecked.
    let relAdded = 0;
    for (let i = 0; i < merge.relationships.length; i++) {
      const r = merge.relationships[i];
      if (excludedRelIdx.has(i)) continue;
      try {
        if (r.status === 'new') {
          if (r.type === 'dependency' || r.type === 'prerequisite') {
            const leftovers = getEdgesForItem(r.sourceItemId).filter(
              (e) =>
                (e.type === 'dependency' || e.type === 'prerequisite') &&
                e.status !== 'dissolved' &&
                e.sourceItemId === r.targetItemId &&
                e.targetItemId === r.sourceItemId,
            );
            for (const leftover of leftovers) {
              try { await dissolveEdge(leftover.id); } catch { /* continue */ }
            }
          }
          const edge = await createEdge({
            type: r.type,
            ...(isSequenceEdgeType(r.type) ? { subtype: DEFAULT_SEQUENCE_STRENGTH } : {}),
            directionality: r.directionality,
            status: 'active',
            declaredMode: 'declared',
            sourceItemId: r.sourceItemId,
            targetItemId: r.targetItemId,
            rationale: 'Re-captured from whiteboard',
            // Uncertainty travels on the recapture path too, matching fresh
            // import (set 2) — parity restored (set 5C).
            metadata: {
              source: 'whiteboard_recapture',
              ...(r.confidence !== undefined ? { confidence: r.confidence } : {}),
              ...(r.basis ? { basis: r.basis } : {}),
            },
            ...(workspaceId && { workspaceId }),
            ...(userId && { createdBy: userId }),
          });
          if (edge) relAdded++;
        } else if (r.status === 'retyped') {
          // Exact pair first; then reverse (legacy inverted Sequence & conditions).
          const existing = getEdgesForItem(r.sourceItemId).find(
            (e) =>
              (e.sourceItemId === r.sourceItemId && e.targetItemId === r.targetItemId) ||
              (e.sourceItemId === r.targetItemId && e.targetItemId === r.sourceItemId),
          );
          if (existing) {
            await updateEdge(existing.id, {
              type: r.type,
              sourceItemId: r.sourceItemId,
              targetItemId: r.targetItemId,
              status: 'active',
              ...(isSequenceEdgeType(r.type) && !existing.subtype ? { subtype: DEFAULT_SEQUENCE_STRENGTH } : {}),
              ...(r.confidence !== undefined || r.basis
                ? { metadata: { ...(existing.metadata ?? {}), source: 'whiteboard_recapture', ...(r.confidence !== undefined ? { confidence: r.confidence } : {}), ...(r.basis ? { basis: r.basis } : {}) } }
                : {}),
            });
          }
        }
      } catch {
        /* continue */
      }
    }

    // Save v2 as the new baseline — reflects what was actually applied:
    // unchecked "new" items/relationships are left out, and an unchecked rename
    // or retype keeps the v1 value.
    try {
      const v2: BoardBaseline = {
        versionId: crypto.randomUUID(),
        capturedAt: new Date().toISOString(),
        items: merge.items
          .filter((i) => !(i.status === 'new' && excludedItemIds.has(i.itemId)))
          .map((i) => ({
            id: i.itemId,
            title: i.status === 'renamed' && excludedItemIds.has(i.itemId) ? i.previousTitle ?? i.title : i.title,
            position: i.position,
            ...(i.code && { code: i.code }),
          })),
        relationships: merge.relationships
          .map((r, i) => ({ r, i }))
          .filter(({ r, i }) => !(r.status === 'new' && excludedRelIdx.has(i)))
          .map(({ r, i }) => ({
            sourceItemId: r.sourceItemId,
            targetItemId: r.targetItemId,
            type: r.status === 'retyped' && excludedRelIdx.has(i) ? r.previousType ?? r.type : r.type,
            directionality: r.directionality,
          })),
      };
      await saveBoardBaseline(board.boardId, board.title, v2);
      void saveBoardCapture(board.boardId, tiles.map((t) => t.dataUrl), board.versionNumber + 1);
    } catch {
      /* baseline save is best-effort */
    }

    setCreated({ items: added, linked: updated, relationships: relAdded, collection: null });
    setProcessing(false);
    setStep('done');
    if (revealEnabled) {
      setRevealScript(buildRevealScriptFromMerge(merge, { title: board.title }, { photoUrl: tiles[0]?.dataUrl }));
      setRevealView('auto');
    }
    onImported?.(added);
    const archivedNote = archived > 0 ? `, ${archived} archived` : '';
    // A re-capture that could not save its new items is not a success.
    const addFailedNote = addFailed > 0
      ? ` — ${addFailed} new item${addFailed !== 1 ? 's' : ''} could not be saved`
      : '';
    addNotification({
      type: addFailed > 0 ? 'warning' : 'success',
      title: `“${board.title}” updated to v${board.versionNumber + 1}`,
      message: `${added} new, ${updated} changed, ${relAdded} new relationship${relAdded !== 1 ? 's' : ''}${archivedNote}${addFailedNote}.`,
      duration: addFailed > 0 ? 8000 : 4000,
    });
  }, [merge, selectedBoardId, needsSignIn, excludedItemIds, excludedRelIdx, archiveRemovedIds, createItem, updateItem, createEdge, updateEdge, dissolveEdge, getEdgesForItem, addNotification, onImported, userId, tiles, revealEnabled]);

  // Number of changes that will be applied given the current diff selections.
  const diffApplyCount = merge
    ? merge.items.filter((i) => i.status !== 'unchanged' && !excludedItemIds.has(i.itemId)).length +
      merge.relationships.filter((r, i) => r.status !== 'unchanged' && !excludedRelIdx.has(i)).length +
      archiveRemovedIds.size
    : 0;

  const signInNotice = needsSignIn ? (
    <div className={`flex items-start gap-2 p-3 ${AMBER_CALLOUT} text-sm ${AMBER_CALLOUT_BODY}`}>
      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <span>
        You're not signed in, so these can't be saved to your workspace.{' '}
        <Link to="/auth/login" className="font-medium underline">Sign in</Link> to import — your photo and edits will be waiting.
      </span>
    </div>
  ) : null;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Import from whiteboard photo"
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== 'analyzing') onClose();
      }}
    >
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden border border-transparent dark:border-[var(--rule)]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 dark:bg-[var(--active-tint)]">
              <Camera className="h-5 w-5 text-primary-600 dark:text-[#8EA7D8]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {mode === 'recapture' ? 'Update Board from Photo' : 'Import from Whiteboard Photo'}
              </h2>
              <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                {step === 'select-board' && 'Pick the board you worked on, then re-photograph it'}
                {step === 'upload' && 'One photo for a board · several overlapping photos for a wall'}
                {step === 'analyzing' && (tiles.length > 1 ? `Stitching ${tiles.length} photos and reading the board…` : 'Reading boxes, stickies, and arrows…')}
                {step === 'preview' && `${newItemCount} new item${newItemCount !== 1 ? 's' : ''}${reusedCount > 0 ? ` · ${reusedCount} reused` : ''} · ${includedRels.length} relationship${includedRels.length !== 1 ? 's' : ''}`}
                {step === 'diff' && merge && `${merge.summary.added} new · ${merge.summary.renamed + merge.summary.moved} changed · ${merge.summary.unchanged} unchanged`}
                {step === 'done' && 'All done'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={step === 'analyzing'}
            className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* ── Select board (re-capture) ── */}
          {step === 'select-board' && (
            <div className="space-y-3">
              {boardsLoading ? (
                <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-8 justify-center">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading your boards…
                </div>
              ) : boards.length === 0 ? (
                <div className="flex flex-col items-center py-8 gap-2 text-center text-[var(--text-ter)]">
                  <Layers className="h-8 w-8 text-[var(--text-ter)]" />
                  <p className="text-sm font-medium text-[var(--text-sec)]">No saved boards yet</p>
                  <p className="text-xs max-w-xs">
                    Import a whiteboard photo first — it's saved as v1, and you can re-photograph it later to capture changes.
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[var(--text-sec)]">
                    Which board did you work on? We'll diff your new photo against its last saved version.
                  </p>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {boards.map((b) => (
                      <button
                        key={b.boardId}
                        type="button"
                        onClick={() => setSelectedBoardId(b.boardId)}
                        className={`w-full flex items-center gap-3 p-3  border text-left transition-colors ${
                          selectedBoardId === b.boardId
                            ? 'border-primary-400 bg-primary-50/50'
                            : 'border-[var(--rule)] hover:border-primary-300 hover:bg-[var(--surface)]'
                        }`}
                      >
                        <Layers className="h-4 w-4 text-primary-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[var(--text-pri)] truncate">{b.title}</p>
                          <p className="text-xs text-[var(--text-ter)]">
                            v{b.versionNumber} · {b.baseline.items.length} items · updated {new Date(b.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                        {selectedBoardId === b.boardId && <CheckCircle className="h-4 w-4 text-primary-600 ml-auto flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Upload ── */}
          {step === 'upload' && (
            <div className="space-y-4">
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onInputChange} />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onInputChange}
              />

              {tiles.length === 0 ? (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDrop}
                  className="flex flex-col items-center gap-3 py-10 border-2 border-dashed border-[var(--rule)]"
                >
                  <Upload className="h-8 w-8 text-[var(--text-ter)]" />
                  <p className="text-sm font-medium text-[var(--text-sec)]">Drag photos here, or</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)]"
                    >
                      <Upload className="h-4 w-4" /> Choose files
                    </button>
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)]"
                    >
                      <Camera className="h-4 w-4" /> Take photo
                    </button>
                    <button
                      type="button"
                      onClick={() => setCoachOpen(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-primary-300 bg-primary-50 hover:bg-primary-100 text-primary-700"
                    >
                      <Video className="h-4 w-4" /> Live capture
                    </button>
                  </div>
                  <p className="text-xs text-[var(--text-ter)]">
                    One photo for a single board · for a big wall, take several overlapping shots.
                    Live capture coaches your framing, light, and focus before each shot.
                  </p>

                  {/* Demo mode: sample boards skip the camera + vision call and
                      run the same plan → preview → commit → reveal pipeline. */}
                  {mode === 'new' && revealEnabled && (
                    <div className="w-full flex flex-col items-center gap-2 pt-3 mt-1 border-t border-dashed border-[var(--rule)]">
                      <p className="text-xs text-[var(--text-ter)]">
                        No whiteboard handy? Try a sample board — same import, no camera or API key needed.
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {DEMO_BOARDS.map((d) => (
                          <button
                            key={d.id}
                            type="button"
                            onClick={() => runDemoBoard(d)}
                            title={d.description}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--rule)] hover:border-primary-300 hover:bg-primary-50/40 text-[var(--text-sec)]"
                          >
                            <Sparkles className="h-3.5 w-3.5 text-primary-500" /> {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
                    {tiles.map((tile, i) => (
                      <div key={tile.id} className="relative overflow-hidden border border-[var(--rule)] bg-[var(--surface)] group">
                        <img src={tile.dataUrl} alt={`Tile ${i + 1}`} className="w-full h-24 object-cover" />
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/55 text-white text-[10px] font-medium">
                          {i + 1}
                        </span>
                        {tile.quality && (
                          <span
                            className={`absolute top-1 right-7 w-2.5 h-2.5 rounded-full ring-2 ring-white ${QUALITY_STYLE[tile.quality.level].dot}`}
                            title={tile.quality.warnings[0] ?? 'Looks good'}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeTile(tile.id)}
                          className="absolute top-1 right-1 p-1 rounded bg-black/55 text-white hover:bg-black/75"
                          aria-label={`Remove tile ${i + 1}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex flex-col items-center justify-center gap-1 h-24 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-primary-400 hover:text-primary-500"
                    >
                      <Plus className="h-5 w-5" />
                      <span className="text-[11px]">Add photo</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCoachOpen(true)}
                      className="flex flex-col items-center justify-center gap-1 h-24 border-2 border-dashed border-primary-300 text-primary-500 hover:border-primary-400 hover:bg-primary-50"
                    >
                      <Video className="h-5 w-5" />
                      <span className="text-[11px]">Live capture</span>
                    </button>
                  </div>

                  {/* Per-tile quality summary */}
                  {poorTileCount > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        {poorTileCount} photo{poorTileCount !== 1 ? 's' : ''} may be hard to read (too dark, far, or blurry).
                        The red-dotted {poorTileCount !== 1 ? 'ones' : 'one'} — consider retaking before analyzing.
                      </span>
                    </div>
                  )}

                  {/* Walk-the-wall guidance */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[var(--text-ter)]">
                    <span>{tiles.length} photo{tiles.length !== 1 ? 's' : ''} added.</span>
                    <label className="flex items-center gap-1.5">
                      Wall width
                      <input
                        type="number"
                        min={0}
                        step={0.5}
                        value={wallWidthM}
                        onChange={(e) => setWallWidthM(e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="m"
                        className="w-16 px-1.5 py-0.5 border border-[var(--rule)] rounded text-[var(--text-sec)]"
                      />
                      m
                    </label>
                    {typeof wallWidthM === 'number' && wallWidthM > 0 && (
                      <span className={tiles.length >= recommendedPhotoCount(wallWidthM) ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}>
                        Recommended ≈ {recommendedPhotoCount(wallWidthM)} (walk left→right, ~30% overlap)
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="border border-[var(--rule)] bg-[var(--surface)] p-3">
                <p className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-sec)] mb-2">
                  <Info className="h-3.5 w-3.5" /> How your drawing maps to the platform
                </p>
                <ul className="space-y-1">
                  {SIGNIFIER_LEGEND.map((l) => (
                    <li key={l.signifier} className="flex items-start gap-2 text-xs text-[var(--text-ter)]">
                      <span className="font-medium text-[var(--text-sec)] min-w-[150px]">{l.signifier}</span>
                      <ArrowRight className="h-3 w-3 mt-0.5 flex-shrink-0 text-[var(--text-ter)]" />
                      <span>{l.mapsTo}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Visual language launcher — set up cue→attribute rules before analyzing */}
              <button
                type="button"
                onClick={() => setLanguageOpen(true)}
                className="flex items-center gap-2 w-full px-3 py-2 border border-[var(--rule)] hover:border-primary-300 hover:bg-primary-50/40 text-sm text-[var(--text-sec)] text-left"
              >
                <Sparkles className="h-4 w-4 text-primary-500 flex-shrink-0" />
                {signifierRules.length > 0 ? (
                  <span>Visual language: <strong className="text-[var(--text-pri)]">{signifierRules.length} rule{signifierRules.length !== 1 ? 's' : ''}</strong> — edit how colors & shapes map to attributes</span>
                ) : (
                  <span>Set up a visual language — map sticky colors, shapes, or lines to attributes (e.g. green → impact 10)</span>
                )}
              </button>

              {/* Claude auth: workspace managed key and/or personal BYOK */}
              {authMode === 'workspace' && (
                <div className="flex flex-col gap-2 p-3 bg-primary-50/60 border border-primary-100 text-sm text-[var(--text-sec)]">
                  <div className="flex items-start gap-2">
                    <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5 text-primary-500" />
                    <span>
                      Using your <strong className="font-medium text-[var(--text-pri)]">workspace Claude key</strong>
                      {' '}— shared access with a monthly free-import quota.
                    </span>
                  </div>
                  {!showByokForm ? (
                    <button
                      type="button"
                      onClick={() => setShowByokForm(true)}
                      className="self-start ml-6 text-xs font-medium text-primary-700 hover:text-primary-800 underline"
                    >
                      Prefer your own key (BYOK)?
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2 pl-6">
                      <p className="text-xs text-[var(--text-sec)]">
                        Paste a personal Anthropic key — kept on our server, encrypted. Analysis will use it instead of the workspace key.
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="password"
                          value={keyInput}
                          onChange={(e) => setKeyInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') void saveKey(); }}
                          placeholder="sk-ant-…"
                          className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-primary-200 bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-300 font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => void saveKey()}
                          disabled={!keyInput.trim()}
                          className="px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50"
                        >
                          Use my key
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowByokForm(false); setKeyInput(''); }}
                          className="px-2 py-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                        >
                          Cancel
                        </button>
                      </div>
                      <span className="text-xs text-[var(--text-ter)]">
                        Or manage keys under{' '}
                        <Link to="/prioritization/settings/my-connectors" className="underline">Settings → My Connectors</Link>.
                      </span>
                    </div>
                  )}
                </div>
              )}

              {authMode === 'byok' && (
                <div className="flex flex-col gap-2 p-3 bg-[var(--surface)] border border-[var(--rule)] text-sm text-[var(--text-sec)]">
                  <div className="flex items-start gap-2">
                    <KeyRound className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
                    <span>
                      Using <strong className="font-medium text-[var(--text-pri)]">your Claude key</strong>
                      {keyPreview ? (
                        <> (<span className="font-mono text-base">{keyPreview}</span>)</>
                      ) : null}
                      {' '}— billed to your Anthropic account.
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-6 text-xs">
                    <button
                      type="button"
                      onClick={() => setShowByokForm((v) => !v)}
                      className="font-medium text-primary-700 hover:text-primary-800 underline"
                    >
                      {showByokForm ? 'Hide' : 'Replace key'}
                    </button>
                    {managedKeyEnabled && (
                      <button
                        type="button"
                        onClick={() => void switchToWorkspaceKey()}
                        className="font-medium text-primary-700 hover:text-primary-800 underline"
                      >
                        Switch to workspace key
                      </button>
                    )}
                    <Link to="/prioritization/settings/my-connectors" className="text-[var(--text-ter)] hover:text-[var(--text-sec)] underline">
                      Manage in Settings
                    </Link>
                  </div>
                  {showByokForm && (
                    <div className="flex items-center gap-2 pl-6">
                      <input
                        type="password"
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') void saveKey(); }}
                        placeholder="sk-ant-…"
                        className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-300 font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => void saveKey()}
                        disabled={!keyInput.trim()}
                        className="px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-500 disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  )}
                </div>
              )}

              {authMode === 'none' && (
                <div className="flex flex-col gap-2 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Photo analysis needs a Claude API key. Paste yours here (BYOK) — kept on our server, encrypted.
                      {' '}<a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" className="font-medium underline">Get a key</a>.
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pl-6">
                    <input
                      type="password"
                      value={keyInput}
                      onChange={(e) => setKeyInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') void saveKey(); }}
                      placeholder="sk-ant-…"
                      className="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-[var(--mustard)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-amber-400 font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => void saveKey()}
                      disabled={!keyInput.trim()}
                      className="px-3 py-1.5 text-sm font-medium bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] disabled:opacity-50"
                    >
                      Save key
                    </button>
                  </div>
                  <span className="pl-6 text-xs text-[var(--mustard)]/80">
                    Or add it under{' '}
                    <Link to="/prioritization/settings/my-connectors" className="underline">Settings → My Connectors</Link>
                    {' '}(Claude card). Ask a workspace admin if a shared Claude key should be enabled for this workspace.
                  </span>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* ── Analyzing ── */}
          {step === 'analyzing' && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
              <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
              <p className="text-sm font-medium text-[var(--text-sec)]">Reading the whiteboard…</p>
              <p className="text-xs text-[var(--text-ter)] max-w-xs">
                Recognizing shapes, transcribing handwriting, and tracing arrows. This usually takes a few seconds.
              </p>
            </div>
          )}

          {/* ── Preview ── */}
          {step === 'preview' && plan && (
            <div className="space-y-5">
              {signInNotice}
              {plan.summary && (
                <div className="bg-primary-50/60 border border-primary-100 px-3 py-2 text-sm text-[var(--text-sec)]">
                  <span className="font-medium">{plan.title || 'Whiteboard'}:</span> {plan.summary}
                </div>
              )}

              {reviewCount > 0 && (
                <div className={`flex items-start gap-2 p-3 ${AMBER_CALLOUT} text-sm ${AMBER_CALLOUT_BODY}`}>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{reviewCount}</strong> transcription{reviewCount !== 1 ? 's were' : ' was'} low-confidence and
                    {reviewCount !== 1 ? ' are' : ' is'} highlighted below — check or correct{' '}
                    {reviewCount !== 1 ? 'them' : 'it'} before importing. Editing a title marks it reviewed.
                  </span>
                </div>
              )}

              {tiles.length === 1 ? (
                <PhotoSelectionOverlay
                  imageUrl={tiles[0].dataUrl}
                  items={plan.items}
                  onToggle={(ref, include) => patchItem(ref, { include })}
                />
              ) : (
                <SpatialMiniMap items={plan.items} />
              )}

              {/* Mapping toggles */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
                  <input
                    type="checkbox"
                    checked={mapColor}
                    onChange={(e) => applyColorMapping(e.target.checked)}
                    className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                  />
                  Map color → category
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
                  <input
                    type="checkbox"
                    checked={mapAltitude}
                    onChange={(e) => applyAltitudeMapping(e.target.checked)}
                    className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                  />
                  Map position → altitude
                </label>
              </div>

              {/* Visual language — the workspace's cue→attribute rules being applied */}
              {signifierRules.length > 0 && (
                <div className="border border-primary-100 bg-primary-50/40 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700 mb-1.5">
                    <Sparkles className="h-3.5 w-3.5" /> Your visual language
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {signifierRules.map((r, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[var(--bg-raised)] border border-primary-200 text-[11px] text-[var(--text-sec)]">
                        {describeSignifierRule(r)}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-[var(--text-ter)] mt-1.5">
                    Configured under Settings → Connectors. Applied automatically to every capture.
                  </p>
                </div>
              )}

              {/* Collection grouping — keeps the board together as a navigable set */}
              <div className="border border-[var(--rule)] p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
                  <input
                    type="checkbox"
                    checked={groupIntoCollection}
                    onChange={(e) => setGroupIntoCollection(e.target.checked)}
                    className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                  />
                  <Layers className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                  Group everything into a collection
                </label>
                {groupIntoCollection && (
                  <>
                    <input
                      type="text"
                      value={collectionName}
                      onChange={(e) => setCollectionName(e.target.value)}
                      placeholder="Collection name"
                      className="w-full px-2.5 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="text-xs text-[var(--text-ter)]">
                      Saved as a curated collection so you can find everything from this board later — reused items are added too.
                    </p>
                  </>
                )}
              </div>

              {/* Items */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">
                  Items — {newItemCount} new{reusedCount > 0 ? `, ${reusedCount} reused` : ''}
                </h3>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {plan.items.map((it) => {
                    const reusing = it.resolution === 'use_existing';
                    // A single-photo capture lets us show a crop of the actual
                    // handwriting beside each row to proofread the transcription.
                    const cropImage = tiles.length === 1 ? tiles[0].dataUrl : null;
                    return (
                      <div
                        key={it.ref}
                        className={`flex flex-col gap-1.5 p-2  border ${
                          !it.include
                            ? 'border-[var(--rule)] bg-[var(--surface)] opacity-60'
                            : it.needsReview
                              ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]/40'
                              : 'border-[var(--rule)] bg-[var(--bg-raised)]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={it.include}
                            onChange={(e) => patchItem(it.ref, { include: e.target.checked })}
                            className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500 flex-shrink-0"
                          />
                          {cropImage && (
                            <PhotoCrop imageUrl={cropImage} position={it.position} title="The handwriting this title was read from" />
                          )}
                          <span
                            className="w-3 h-3 border border-[var(--rule)] flex-shrink-0"
                            style={{ background: COLOR_SWATCH[it.color] ?? COLOR_SWATCH.unknown }}
                            title={it.color}
                          />
                          <input
                            type="text"
                            value={it.title}
                            // Editing a flagged title counts as reviewing it.
                            onChange={(e) => patchItem(it.ref, { title: e.target.value, needsReview: false })}
                            disabled={reusing}
                            className={`flex-1 min-w-0 px-2 py-1 text-sm border rounded focus:outline-none focus:border-primary-400 disabled:text-[var(--text-ter)] disabled:line-through ${
                              it.needsReview ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]' : 'border-transparent hover:border-[var(--rule)]'
                            }`}
                          />
                          <select
                            value={it.itemCategory ?? ''}
                            onChange={(e) =>
                              patchItem(it.ref, { itemCategory: (e.target.value || undefined) as ItemLanguageType | undefined })
                            }
                            disabled={reusing}
                            className="text-xs border border-[var(--rule)] rounded px-1.5 py-1 text-[var(--text-sec)] flex-shrink-0 disabled:opacity-40"
                            title="Item category"
                          >
                            <option value="">— category —</option>
                            {CATEGORY_OPTIONS.map((c) => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                          <select
                            value={it.altitudeLevel ?? ''}
                            onChange={(e) =>
                              patchItem(it.ref, {
                                altitudeLevel: e.target.value ? (Number(e.target.value) as AltitudeLevel) : undefined,
                              })
                            }
                            disabled={reusing}
                            className="text-xs border border-[var(--rule)] rounded px-1.5 py-1 text-[var(--text-sec)] flex-shrink-0 w-14 disabled:opacity-40"
                            title="Altitude level"
                          >
                            <option value="">alt</option>
                            {[5, 4, 3, 2, 1].map((a) => (
                              <option key={a} value={a}>{a}</option>
                            ))}
                          </select>
                        </div>

                        {/* Attributes assigned by the workspace's visual language (e.g. impact 10) */}
                        {it.metadata && Object.keys(it.metadata).length > 0 && (
                          <div className="flex flex-wrap items-center gap-1 pl-6">
                            {Object.entries(it.metadata).map(([k, v]) => (
                              <span
                                key={k}
                                className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 text-[11px] font-medium"
                                title="Set by your visual-language mapping"
                              >
                                {k.replace(/^attribute:/, '')}: {String(v)}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Low-confidence review — shown until the user edits or confirms the title */}
                        {it.needsReview && (
                          <div className="flex items-center gap-2 pl-6 text-xs">
                            <span className="px-1.5 py-0.5 rounded font-medium bg-[var(--mustard-dim)] text-[var(--mustard)]">
                              Check transcription
                            </span>
                            <span className="text-[var(--text-ter)]">{Math.round(it.confidence * 100)}% sure</span>
                            <button
                              type="button"
                              onClick={() => patchItem(it.ref, { needsReview: false })}
                              className="ml-auto inline-flex items-center gap-1 text-[var(--green)] dark:text-[var(--text-ter)] hover:underline"
                            >
                              <CheckCircle className="h-3.5 w-3.5" /> Looks right
                            </button>
                          </div>
                        )}

                        {/* Duplicate resolution — shown when this box matches an existing item */}
                        {it.duplicateOf && (
                          <div className="flex items-center gap-2 pl-6 text-xs">
                            <span
                              className={`px-1.5 py-0.5 rounded font-medium ${
                                it.duplicateOf.confidence === 'exact'
                                  ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                                  : 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                              }`}
                            >
                              {it.duplicateOf.confidence === 'exact' ? 'Duplicate of' : 'Possible match'}
                            </span>
                            <span className="text-[var(--text-ter)] truncate max-w-[180px]" title={it.duplicateOf.existingTitle}>
                              “{it.duplicateOf.existingTitle}”
                            </span>
                            <div className="ml-auto inline-flex border border-[var(--rule)] overflow-hidden">
                              {(['create', 'use_existing'] as const).map((r) => (
                                <button
                                  key={r}
                                  type="button"
                                  onClick={() => patchItem(it.ref, { resolution: r })}
                                  className={`px-2 py-0.5 ${
                                    it.resolution === r
                                      ? 'bg-primary-600 text-white'
                                      : 'bg-[var(--bg-raised)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                                  }`}
                                >
                                  {r === 'create' ? 'Create new' : 'Use existing'}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Relationships */}
              {plan.relationships.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">
                    Relationships ({includedRels.length})
                  </h3>
                  <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                    {plan.relationships.map((rel) => {
                      const src = itemsByRef.get(rel.sourceRef);
                      const tgt = itemsByRef.get(rel.targetRef);
                      const bothIncluded = src?.include && tgt?.include;
                      return (
                        <div
                          key={rel.ref}
                          className={`flex items-center gap-2 p-2  border text-sm ${
                            rel.include && bothIncluded ? 'border-[var(--rule)] bg-[var(--bg-raised)]' : 'border-[var(--rule)] bg-[var(--surface)] opacity-60'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={rel.include}
                            disabled={!bothIncluded}
                            onChange={(e) => patchRel(rel.ref, { include: e.target.checked })}
                            className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500 flex-shrink-0"
                          />
                          <span className="truncate max-w-[140px] text-[var(--text-sec)]" title={src?.title}>{src?.title ?? '?'}</span>
                          {rel.directionality === 'directed' ? (
                            <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                          ) : (
                            <ArrowLeftRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                          )}
                          <span className="truncate max-w-[140px] text-[var(--text-sec)]" title={tgt?.title}>{tgt?.title ?? '?'}</span>
                          {rel.fromSymbol && rel.symbol && (
                            <span
                              className="px-1.5 py-0.5 rounded bg-[var(--slate-dim)] text-[var(--text-sec)] text-[11px] font-medium flex-shrink-0"
                              title={`Relationship type read from the marker “${rel.symbol}” on the connector`}
                            >
                              “{rel.symbol}”
                            </span>
                          )}
                          <span
                            className={`px-1.5 py-0.5 rounded text-[11px] font-mono flex-shrink-0 ${
                              rel.confidence >= 0.85 ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                            }`}
                            title={`Type inferred from ${rel.basis.replace('_', ' ')} at ${Math.round(rel.confidence * 100)}% confidence — recorded on the relationship`}
                          >
                            {Math.round(rel.confidence * 100)}%
                          </span>
                          <select
                            value={rel.type}
                            onChange={(e) => patchRel(rel.ref, { type: e.target.value as RelationshipEdgeType })}
                            className="ml-auto text-xs border border-[var(--rule)] rounded px-1.5 py-1 text-[var(--text-sec)] flex-shrink-0"
                          >
                            {RELATIONSHIP_OPTIONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <UnrecognizedList marks={plan.unrecognized} onResolve={resolveUnrecognized} />

              {plan.warnings.length > 0 && (
                <div className={`flex items-start gap-2 p-3 ${AMBER_CALLOUT} text-xs ${AMBER_CALLOUT_BODY}`}>
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    {plan.warnings.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {error}
                </div>
              )}
            </div>
          )}

          {/* ── Diff (re-capture v1 → v2) ── */}
          {step === 'diff' && merge && (
            <div className="space-y-4">
              {signInNotice}
              <DiffReveal summary={merge.summary} />

              {registered && (
                <div className="bg-[var(--green-dim)] border border-[var(--green)] px-3 py-2 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                  Corner registration marks detected — new shapes were aligned to the board's frame, so positions are accurate regardless of the camera angle.
                </div>
              )}

              {signifierRules.length > 0 && (
                <div className="border border-primary-100 bg-primary-50/40 px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700 mb-1">
                    <Sparkles className="h-3.5 w-3.5" /> Visual language applied to new boxes
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {signifierRules.map((r, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[var(--bg-raised)] border border-primary-200 text-[11px] text-[var(--text-sec)]">
                        {describeSignifierRule(r)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <UnrecognizedList marks={recaptureUnrecognized} onResolve={resolveRecaptureUnrecognized} />

              {(() => {
                const titleOf = (id: string) =>
                  merge.items.find((i) => i.itemId === id)?.title ??
                  merge.removedItems.find((i) => i.id === id)?.title ??
                  id.slice(0, 6);
                const badge: Record<string, string> = {
                  new: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
                  renamed: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
                  moved: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
                  retyped: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
                };
                const changedItems = merge.items.filter((i) => i.status !== 'unchanged');
                const changedRels = merge.relationships
                  .map((r, idx) => ({ r, idx }))
                  .filter(({ r }) => r.status !== 'unchanged');
                return (
                  <div className="space-y-4">
                    {changedItems.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">Item changes</h3>
                        <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                          {changedItems.map((i) => {
                            const included = !excludedItemIds.has(i.itemId);
                            return (
                              <label key={i.itemId} className={`flex items-center gap-2 text-sm cursor-pointer ${included ? '' : 'opacity-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={() => setExcludedItemIds((prev) => {
                                    const n = new Set(prev);
                                    if (n.has(i.itemId)) n.delete(i.itemId); else n.add(i.itemId);
                                    return n;
                                  })}
                                  className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500 flex-shrink-0"
                                />
                                <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${badge[i.status]}`}>{i.status}</span>
                                {i.matchedByCode && i.code && (
                                  <span className="px-1 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)] font-mono text-[10px] flex-shrink-0" title="Matched exactly by anchor code">{i.code}</span>
                                )}
                                <span className="text-[var(--text-pri)] truncate">{i.title}</span>
                                {i.status === 'renamed' && i.previousTitle && (
                                  <span className="text-xs text-[var(--text-ter)] truncate">was “{i.previousTitle}”</span>
                                )}
                                {i.metadata && Object.entries(i.metadata).map(([k, v]) => (
                                  <span key={k} className="px-1.5 py-0.5 rounded bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-[10px] font-medium flex-shrink-0" title="Set by your visual-language mapping">
                                    {k.replace(/^attribute:/i, '')}: {String(v)}
                                  </span>
                                ))}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {changedRels.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-2">Relationship changes</h3>
                        <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                          {changedRels.map(({ r, idx }) => {
                            const included = !excludedRelIdx.has(idx);
                            return (
                              <label key={idx} className={`flex items-center gap-2 text-sm cursor-pointer ${included ? '' : 'opacity-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={() => setExcludedRelIdx((prev) => {
                                    const n = new Set(prev);
                                    if (n.has(idx)) n.delete(idx); else n.add(idx);
                                    return n;
                                  })}
                                  className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500 flex-shrink-0"
                                />
                                <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${badge[r.status]}`}>{r.status}</span>
                                <span className="text-[var(--text-sec)] truncate max-w-[110px]">{titleOf(r.sourceItemId)}</span>
                                <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                                <span className="text-[var(--text-sec)] truncate max-w-[110px]">{titleOf(r.targetItemId)}</span>
                                <span className="ml-auto text-xs text-[var(--text-ter)]">
                                  {r.type}{r.status === 'retyped' && r.previousType ? ` (was ${r.previousType})` : ''}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {(merge.removedItems.length > 0 || merge.removedRelationships.length > 0) && (
                      <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3 space-y-2">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-[var(--mustard)]">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          Not on the new photo — left untouched unless you archive them
                        </p>
                        {merge.removedItems.map((it) => (
                          <label key={it.id} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={archiveRemovedIds.has(it.id)}
                              onChange={() => setArchiveRemovedIds((prev) => {
                                const n = new Set(prev);
                                if (n.has(it.id)) n.delete(it.id); else n.add(it.id);
                                return n;
                              })}
                              className="rounded border-[var(--rule)] text-[var(--mustard)] focus:ring-amber-500 flex-shrink-0"
                            />
                            <span className="text-[var(--text-sec)] truncate">{it.title}</span>
                            <span className="ml-auto text-xs text-[var(--mustard)]">{archiveRemovedIds.has(it.id) ? 'will archive' : 'keep'}</span>
                          </label>
                        ))}
                        {merge.removedRelationships.length > 0 && (
                          <p className="text-xs text-[var(--mustard)]">
                            {merge.removedRelationships.length} relationship{merge.removedRelationships.length !== 1 ? 's' : ''} also gone from the board (left untouched).
                          </p>
                        )}
                      </div>
                    )}

                    {changedItems.length === 0 && changedRels.length === 0 && (
                      <p className="text-sm text-[var(--text-ter)] py-4 text-center">No changes detected — the board matches its saved version.</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Done ── */}
          {step === 'done' && (
            <div className="flex flex-col items-center py-8 gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-[var(--green-dim)] flex items-center justify-center">
                <CheckCircle className="h-7 w-7 text-[var(--green)]" />
              </div>
              <div>
                {mode === 'recapture' ? (
                  <>
                    <p className="text-base font-semibold text-[var(--text-pri)]">Board updated</p>
                    <p className="text-sm text-[var(--text-ter)] mt-1">
                      {created.items} new item{created.items !== 1 ? 's' : ''}, {created.linked} changed
                      {created.relationships > 0 && <>, {created.relationships} new relationship{created.relationships !== 1 ? 's' : ''}</>}. Saved as the new version.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-base font-semibold text-[var(--text-pri)]">
                      {created.items} item{created.items !== 1 ? 's' : ''} imported
                    </p>
                    <p className="text-sm text-[var(--text-ter)] mt-1">
                      {created.relationships > 0
                        ? `${created.relationships} relationship${created.relationships !== 1 ? 's' : ''} created from your arrows and groupings.`
                        : 'Items are now in your library.'}
                      {created.linked > 0 && (
                        <> {created.linked} existing item{created.linked !== 1 ? 's were' : ' was'} reused instead of duplicated.</>
                      )}
                    </p>
                    {created.collection && (
                      <p className="flex items-center justify-center gap-1.5 text-sm text-[var(--text-sec)] mt-2">
                        <Layers className="h-4 w-4 text-primary-500" />
                        Grouped as the collection “{created.collection}”.
                      </p>
                    )}
                    {undoRecord && undoRecord.itemIds.length > 0 && (
                      <div className="mt-3">
                        <ImportBatchActions
                          batchId={undoRecord.batchId}
                          itemIds={undoRecord.itemIds}
                          onNavigate={onClose}
                          beforeUndo={beforeUndoImport}
                          onUndone={(outcome) => void afterUndoImport(outcome)}
                        />
                      </div>
                    )}
                    {undone && (
                      <p className="text-sm text-[var(--text-ter)] mt-3 italic">
                        This import was undone. Its items left every list; their records and history stay.
                      </p>
                    )}
                  </>
                )}
                {revealScript && !undone && (
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setRevealView('auto')}
                      className="btn btn-secondary text-sm flex items-center gap-1.5"
                      title="Replay the reveal animation"
                    >
                      <Sparkles className="h-4 w-4" /> Replay reveal
                    </button>
                    <button
                      type="button"
                      onClick={() => setRevealView('walkthrough')}
                      className="btn btn-secondary text-sm flex items-center gap-1.5"
                      title="Step through what this import did, scene by scene"
                    >
                      <Play className="h-4 w-4" /> Play what happened
                    </button>
                    <button
                      type="button"
                      onClick={() => setRevealView('diagram')}
                      className="btn btn-secondary text-sm flex items-center gap-1.5"
                      title="See (and export) the imported structure as a Peak Priorities diagram"
                    >
                      <LayoutGrid className="h-4 w-4" /> View diagram
                    </button>
                    {revealScript.nodes.length > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate('/prioritization/sessions/new', {
                            state: { prefill: sessionPrefillFromReveal(revealScript, 'whiteboard_import') },
                          })
                        }
                        className="btn btn-primary text-sm flex items-center gap-1.5"
                        title="Open the session wizard with these items pre-selected"
                      >
                        <ArrowRight className="h-4 w-4" /> Rank these in a session
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-between">
          {step === 'analyzing' ? (
            <button
              type="button"
              onClick={() => { abortRef.current?.abort(); setStep('upload'); }}
              className="btn btn-secondary text-sm"
            >
              Cancel
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (step === 'preview') { setStep('upload'); setPlan(null); }
                else if (step === 'diff') { setStep('upload'); setMerge(null); }
                else if (step === 'upload' && mode === 'recapture') { setStep('select-board'); setTiles([]); }
                else onClose();
              }}
              className="btn btn-secondary text-sm"
            >
              {step === 'preview' || step === 'diff'
                ? 'Back'
                : step === 'done'
                  ? 'Close'
                  : step === 'upload' && mode === 'recapture'
                    ? 'Back'
                    : returnToSourceChooser && (step === 'upload' || step === 'select-board')
                      ? 'Select another tool'
                      : 'Cancel'}
            </button>
          )}

          {step === 'select-board' && (
            <button
              type="button"
              onClick={() => setStep('upload')}
              disabled={!selectedBoardId}
              className="btn btn-primary flex items-center gap-2"
            >
              <ArrowRight className="h-4 w-4" /> Continue
            </button>
          )}

          {step === 'upload' && (
            <button
              type="button"
              onClick={runAnalysis}
              disabled={tiles.length === 0 || !canAnalyze}
              className="btn btn-primary flex items-center gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              {tiles.length > 1 ? `Analyze ${tiles.length} photos` : 'Analyze photo'}
            </button>
          )}

          {step === 'diff' && merge && (
            <button
              type="button"
              onClick={applyMerge}
              disabled={processing || diffApplyCount === 0 || needsSignIn}
              className="btn btn-primary flex items-center gap-2"
            >
              {processing && <Loader2 className="h-4 w-4 animate-spin" />}
              {processing ? 'Applying…' : diffApplyCount > 0 ? `Apply ${diffApplyCount} change${diffApplyCount !== 1 ? 's' : ''}` : 'No changes selected'}
            </button>
          )}

          {step === 'preview' && (
            <div className="flex items-center gap-3">
              {reviewCount > 0 && (
                <span className="text-xs text-[var(--mustard)] inline-flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" /> {reviewCount} to review
                </span>
              )}
              <button
                type="button"
                onClick={confirmImport}
                disabled={processing || includedItems.length === 0 || needsSignIn}
                className="btn btn-primary flex items-center gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                {processing
                  ? 'Importing…'
                  : newItemCount > 0
                    ? `Import ${newItemCount} item${newItemCount !== 1 ? 's' : ''}`
                    : `Add ${includedRels.length} relationship${includedRels.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          )}

          {step === 'done' && (
            <button type="button" onClick={onClose} className="btn btn-primary">
              Done
            </button>
          )}
        </div>
      </div>

      {coachOpen && (
        <WhiteboardCaptureCoach
          onCapture={addDataUrlTile}
          onClose={() => setCoachOpen(false)}
        />
      )}

      {languageOpen && (
        <WhiteboardLanguageEditor
          onClose={() => setLanguageOpen(false)}
          onSaved={(rules) => setSignifierRules(rules)}
        />
      )}

      {revealScript && revealView && (
        <WhiteboardRevealOverlay
          script={revealScript}
          mode={revealView}
          onClose={() => setRevealView(null)}
        />
      )}
    </div>
  );
}
